// shared-lib/cabinet-runs/bays.js
//
// The appliance-bay model. A run is an ordered sequence of BAYS along its
// segments; cabinet / filler / corner bays are disposable solve outputs, but
// an APPLIANCE bay is persisted: it is a fixed-width, hard-centred piece the
// packer tiles around (exactly the reservation `run.appliances[]` used to be)
// that now carries identity — its kind, the appliance object it holds and
// that object's fixture group — so the run can MOVE those objects to the
// bay's solved centre instead of leaving them behind or re-emitting them.
//
// `run.appliances[]` is the legacy shape. `baysForRun` migrates it at read
// time (pure, deterministic ids) and `storedRunFromRun` writes `bays` back,
// the way `pins` → `bayOverrides` already migrates. Producers that still emit
// `appliances[]` (the auto-kitchen `toRuns`, `deriveScanRuns`) keep working
// unchanged. See reference-docs/layout-relationships/overview.md §2.6.

const {
  APPLIANCE_FOOTPRINTS,
} = require('../kitchen-assembly/candidates/applianceRequirements');
const { profileOf, runDepthSpec } = require('./profiles');
const { isVanityRunId } = require('./runIds');

const IN = 0.0254;

const BAY_KINDS = [
  'cabinet',
  'filler',
  'corner',
  'clearance',
  'sink-base',
  'dishwasher',
  'range',
  'cooktop',
  'fridge',
  'tallUnit',
];

// Run-vocabulary appliance type → bay kind. `toRunApplianceType` already
// normalises object types into the left column.
const APPLIANCE_KIND_BY_RUN_TYPE = {
  sink: 'sink-base',
  dishwasher: 'dishwasher',
  range: 'range',
  stove: 'range',
  // A cooktop is its OWN kind, not a range: the run builds a base cabinet
  // under it and runs the counter over it. See `COOKTOP_HOSTED` below.
  cooktop: 'cooktop',
  fridge: 'fridge',
  refrigerator: 'fridge',
  tallUnit: 'tallUnit',
};
const RUN_TYPE_BY_BAY_KIND = {
  'sink-base': 'sink',
  dishwasher: 'dishwasher',
  range: 'range',
  cooktop: 'cooktop',
  fridge: 'fridge',
  tallUnit: 'tallUnit',
};

// Bay kinds whose span is filled with a BASE CABINET the run builds, rather
// than left as a void for a floor-standing appliance. A sink drops into its
// base and a cooktop is cut into the counter over its base; a range, a fridge
// and a pantry stand on the floor and own their whole span.
const HOSTED_BAY_KINDS = new Set(['sink-base', 'cooktop']);

// Bay kinds whose object an INCUMBENT pass seats (`emitTallUnits`,
// `emitOvenColumns`) rather than the appliance mover. The two agree on where
// a bay the run CAN place goes — both use the solved centre — and part company
// only on one the run reports UNSATISFIED, where the incumbent pass holds the
// measured body at its anchor. The mover must respect that, or it undoes the
// hold: `applyApplianceMoves` runs AFTER the incumbent passes
// (`mergeResolvedRuns.js`), and `applianceObjectFor`'s first branch is
// unfiltered, so it finds the measured CABINET a `tallUnit` bay names (36 of
// 37 tall bays on dev are `lower-cabinet`) and drags it to the solved centre —
// after the pass has already warned that it held it (#2020: the 24.4" column
// split on 26767201, the 114" pantry on Montrose).
//
// Deliberately NOT a blanket skip. The drag ghost
// (`previewForRunEdit` → the mover, with no incumbent passes) and the commit
// (the merge, with them) must agree in BOTH directions, so a SATISFIED tall
// unit still moves here. The scan site never reached this either way —
// `slideSinksToBays` passes `sink-base` bays only.
const INCUMBENT_SEATED_BAY_KINDS = new Set(['tallUnit']);

/** True for a bay kind whose object an incumbent pass owns — see above. */
const isIncumbentSeatedKind = (kind) => INCUMBENT_SEATED_BAY_KINDS.has(kind);

/**
 * Standard sink-base cabinet widths, narrowest first: the base a SCANNED bowl
 * is presumed to sit in, and a vanity's outside a line. A kitchen built in a
 * line picks from the line's own sizes instead (`sinkBaseWidths.js`) — kept
 * apart so a scan's reservation does not move when a line's catalog does.
 */
const SINK_BASE_WIDTHS_IN = [24, 30, 36];
/**
 * Cabinetry either side of the bowl: box sides plus reveal. A basin needs it
 * to land in a base at all — a 32" bowl over a 27" base overhangs its own
 * cabinet by 2½" a side and laps onto the fillers beside it, which is what
 * Golden West / Scan 1's island had.
 */
const SINK_BASE_SIDE_ALLOWANCE_IN = 3;

/**
 * The base cabinet width for a bowl measuring `bowlWidth` metres: the
 * narrowest standard base that clears the bowl by
 * `SINK_BASE_SIDE_ALLOWANCE_IN`, capped at 36".
 *
 * The allowance is the point — a 24" bowl in a 24" base has no stiles and
 * cannot be built, and 1½" a side is the bare minimum that can. Capped rather
 * than extrapolated because 36" is the widest standard base; a bowl beyond
 * that is a farmhouse/apron special, sold to sit in a 36" base, and reserving
 * more would eat the run.
 */
function sinkBaseWidthFor(bowlWidth) {
  return sinkBaseFromWidths(bowlWidth, SINK_BASE_WIDTHS_IN);
}

/**
 * The same rule over a given size list (inches, narrowest first): the
 * narrowest that clears the bowl by `SINK_BASE_SIDE_ALLOWANCE_IN`, else the
 * widest. Metres in, metres out.
 */
function sinkBaseFromWidths(bowlWidth, widthsIn) {
  const needed = (bowlWidth || 0) / IN + SINK_BASE_SIDE_ALLOWANCE_IN;
  const fit = widthsIn.find((w) => w >= needed - 1e-6);
  return (fit ?? widthsIn[widthsIn.length - 1]) * IN;
}

// A wall oven reaches a run as a `tallUnit` anchor (see
// `wallToRun.APPLIANCE_TYPE_MAP`): the run RESERVES its column and never builds
// it, so the object keeps its own height and its WALL_OVEN_BOTTOM_Y seat.
// `footprintFor('wallOven')` still throws — nothing should ask for one by that
// name, and the unknown-type contract is what catches it if something does.
const FOOTPRINT_BY_TYPE = {
  // A TALL UNIT is not an appliance, but it reserves span the same way: a
  // pantry or an oven column is cabinetry the fill must pack around, never
  // through. It is anchored (not built) by the run — the scanned object keeps
  // its own height, which is the whole reason it is not an ordinary bay — so
  // this entry only supplies defaults for an anchor that omits a footprint.
  tallUnit: { width: 24 * IN, depth: 24 * IN, height: 84 * IN },
  sink: APPLIANCE_FOOTPRINTS.sink,
  dishwasher: APPLIANCE_FOOTPRINTS.dishwasher,
  range: APPLIANCE_FOOTPRINTS.stoveStandard,
  stove: APPLIANCE_FOOTPRINTS.stoveStandard,
  cooktop: APPLIANCE_FOOTPRINTS.cooktop,
  fridge: APPLIANCE_FOOTPRINTS.fridgeStandard,
  refrigerator: APPLIANCE_FOOTPRINTS.fridgeStandard,
};

function footprintFor(type) {
  const fp = FOOTPRINT_BY_TYPE[type];
  if (!fp) throw new Error(`Unknown appliance type: ${type}`);
  return fp;
}

/**
 * Footprint for a run appliance ANCHOR. The auto-kitchen path stores the
 * chosen tier (a pro stove, a wide fridge) on the anchor as `footprint`;
 * honour it so a large appliance reserves + renders at its true width.
 * Manual-drawn anchors have no `footprint` and fall back to the by-type tier.
 *
 * `profile` is the run's dimension profile (`profiles.js`). `FOOTPRINT_BY_TYPE`
 * is the KITCHEN's table — 36" wide, 24" deep, 34.5" tall for a sink base —
 * so a profile that builds different bodies says so in
 * `profile.applianceFootprints`, which is consulted between the anchor's own
 * footprint and the table. Without it a vanity run's sink bay reserved a
 * kitchen-sized hole: 36" of span for a 30" base, and 24"×34.5" of body under
 * a cabinet the same fill then built at 21"×32".
 *
 * The anchor's OWN footprint still wins over both — a measured or chosen size
 * is not a default.
 */
function footprintForAppliance(a, profile = null, runDepth = null) {
  const base =
    profile?.applianceFootprints?.[a?.type] || FOOTPRINT_BY_TYPE[a?.type];
  const fp = a?.footprint;
  if (!base && !fp) throw new Error(`Unknown appliance type: ${a?.type}`);
  // A HOSTED bay is a base cabinet the run BUILDS (a sink base, a cooktop
  // base), so its reserved depth is the run's body depth — including a depth
  // the user chose (`run.depth`). Without this a run set to Shallow reserved
  // a standard-depth hole under the shallow cabinet the same fill then built
  // in it. A floor-standing appliance (range, fridge) keeps its OWN depth:
  // that is the product's size, not the cabinetry's.
  const hosted = HOSTED_BAY_KINDS.has(APPLIANCE_KIND_BY_RUN_TYPE[a?.type]);
  const depth = fp?.depth ?? (hosted && runDepth > 0 ? runDepth : base?.depth);
  return {
    width: fp?.width ?? base?.width,
    depth,
    height: fp?.height ?? base?.height,
  };
}

const isApplianceKind = (kind) => !!RUN_TYPE_BY_BAY_KIND[kind];

/** A legacy `run.appliances[]` entry as a bay. Deterministic id. */
function applianceBayFromLegacy(a, profile = null, runDepth = null) {
  const kind = APPLIANCE_KIND_BY_RUN_TYPE[a.type];
  if (!kind) throw new Error(`Unknown appliance type: ${a?.type}`);
  const fp = footprintForAppliance(a, profile, runDepth);
  const bay = {
    id: `bay_${a.id}`,
    kind,
    segmentIndex: a.segmentIndex ?? 0,
    anchorParam: a.centerParam,
    width: fp.width,
    applianceId: a.id,
    hard: true,
  };
  if (fp.depth != null) bay.depth = fp.depth;
  if (fp.height != null) bay.height = fp.height;
  if (a.fixtureGroupId) bay.fixtureGroupId = a.fixtureGroupId;
  // An oven column is three objects answering to one bay; the spec is what
  // says so (proposal §9 step 7). Carried verbatim — this shape is the
  // deriver's, and `bays.js` must not have an opinion about it.
  if (a.column) bay.column = a.column;
  // A fridge the kitchen stood free (`fridgeIsBuiltIn`). Only the `false` is
  // ever written: every other fridge bay is built in, as it always was.
  if (a.builtIn === false) bay.builtIn = false;
  return bay;
}

/** The inverse: a bay as the legacy anchor `buildApplianceObjects` reads. */
function legacyApplianceFromBay(b) {
  const type = RUN_TYPE_BY_BAY_KIND[b.kind];
  if (!type) throw new Error(`Not an appliance bay: ${b?.kind}`);
  const footprint = { width: b.width };
  if (b.depth != null) footprint.depth = b.depth;
  if (b.height != null) footprint.height = b.height;
  const a = {
    id: b.applianceId,
    type,
    segmentIndex: b.segmentIndex,
    centerParam: b.anchorParam,
    footprint,
  };
  if (b.fixtureGroupId) a.fixtureGroupId = b.fixtureGroupId;
  if (b.column) a.column = b.column;
  if (b.builtIn === false) a.builtIn = false;
  return a;
}

/**
 * Does a bay hold this OBJECT? The one bay↔object matcher (#1527 — the web
 * `runBayForObject`, the mover's `applianceObjectFor` and the pin lookup
 * each carried a copy): by the object's `bayId` stamp, else by the object's
 * own id (a bay's id / applianceId IS the appliance object's id on derived
 * runs, and rooms stored before the stamp existed carry no `bayId`).
 */
function bayMatchesObject(bay, object) {
  if (!bay || !object) return false;
  return (
    bay.id === object.bayId ||
    bay.id === object.id ||
    bay.applianceId === object.id
  );
}

/** A run's bays: `run.bays` as-is, else the legacy `appliances[]` migrated. */
function baysForRun(run) {
  if (Array.isArray(run?.bays)) return run.bays;
  // The run's profile sizes a bay the migration is MINTING. A run that
  // already carries `bays` is returned untouched, as it always was: a stored
  // bay is a decision, and re-defaulting one under a live run would move
  // cabinetry the user has already placed.
  const profile = profileOf(run);
  const depth = runDepthSpec(run);
  return (run?.appliances || []).map((a) =>
    applianceBayFromLegacy(a, profile, depth)
  );
}

/**
 * The run bay an object is anchored by — by the `bayId` stamp, else by the
 * object's own id (a bay's id / applianceId IS the appliance object's id on
 * derived runs). One matcher for the 2D bay drag and the panel's mode row.
 * @returns {{ stored, bay }|null}
 */
function runBayForObject(room, object) {
  if (!object?.id && !object?.bayId) return null;
  for (const stored of room?.cabinetRuns || []) {
    const bay = baysForRun(stored).find(
      (b) => isApplianceKind(b.kind) && bayMatchesObject(b, object)
    );
    if (bay) return { stored, bay };
  }
  return null;
}

/**
 * Appliance bays on one segment. A bay set aside (`unplaced`) is skipped, and
 * so is one whose appliance is gone (`dangling`, set by
 * bayFootprint.resolveRunBayFootprints) — it must not reserve span for an
 * object that no longer exists.
 */
// Hard bays that hold a span OPEN rather than fill it: a corner body the run
// does not own (`corner`) and floor the layout keeps clear — a walkway, the
// island's aisle, a wall solid, an opening's zone (`clearance`). Neither is
// an appliance, so `applianceBays` never sees them; the packer partitions
// around them and emits nothing there.
const KEEP_OUT_BAY_KINDS = new Set(['corner', 'clearance']);
function keepOutBays(run, segIndex) {
  return baysForRun(run).filter(
    (b) =>
      KEEP_OUT_BAY_KINDS.has(b.kind) &&
      (b.segmentIndex ?? 0) === segIndex &&
      b.width > 0
  );
}

function applianceBays(run, segIndex) {
  return baysForRun(run).filter(
    (b) =>
      isApplianceKind(b.kind) &&
      !b.unplaced &&
      !b.dangling &&
      (b.segmentIndex ?? 0) === segIndex
  );
}

/**
 * Appliance ids the FREESTANDING runs claim. A freestanding run's claim on
 * an appliance outranks a wall run's - an island's sink can never be
 * reseated to a wall.
 */
function freestandingApplianceClaims(runs) {
  const claims = new Set();
  for (const r of runs || []) {
    if (!(r?.segments || []).some((seg) => seg?.freestanding)) continue;
    for (const b of baysForRun(r)) {
      if (b?.applianceId && isApplianceKind(b.kind)) claims.add(b.applianceId);
    }
  }
  return claims;
}

/**
 * Heal double-claimed bays across a room's runs (found live 2026-08-31: a
 * phantom wall-run bay named the ISLAND's sink, so every wall re-solve
 * dragged the island toward the wall). Rules: within a run, one bay per id;
 * across runs, the freestanding claim wins and the wall run loses its bay.
 * Pure; identity when nothing is double-claimed.
 */
function healRunBayClaims(runs) {
  const list = Array.isArray(runs) ? runs : [];
  const claims = freestandingApplianceClaims(list);
  let changed = false;
  const out = list.map((r) => {
    const free = (r?.segments || []).some((seg) => seg?.freestanding);
    const bays = baysForRun(r);
    const seen = new Set();
    const kept = bays.filter((b) => {
      if (b?.id) {
        if (seen.has(b.id)) {
          changed = true;
          return false;
        }
        seen.add(b.id);
      }
      if (
        !free &&
        b?.applianceId &&
        isApplianceKind(b.kind) &&
        claims.has(b.applianceId)
      ) {
        changed = true;
        return false;
      }
      return true;
    });
    if (kept.length === bays.length) return r;
    const { appliances: _legacy, ...rest } = r;
    return { ...rest, bays: kept };
  });
  return changed ? out : list;
}

/**
 * The runs after `deletedObjects` left the room: a floor-standing appliance
 * bay whose appliance is among them goes with it, so the span fills on the
 * next re-solve instead of holding a hole for an object that no longer
 * exists (#1754). Hosted bays (sink-base, cooktop) stay — their base cabinet
 * is the run's, not the basin's. Matches through `bayMatchesObject`, the one
 * bay↔object rule. Pure; identity when nothing changes.
 *
 * Given what REMAINS in the room, a vanity run (`vanityToRun`) the delete
 * left with no object goes too (`withoutEmptiedVanityRuns`).
 */
function runsAfterObjectsDeleted(runs, deletedObjects, remainingObjects) {
  const gone = (deletedObjects || []).filter(Boolean);
  if (!Array.isArray(runs) || !gone.length) return runs;
  const kept = withoutEmptiedVanityRuns(runs, gone, remainingObjects);
  return withoutDeletedApplianceBays(kept, gone);
}

/**
 * A VANITY run whose last objects a delete took: the web's "Delete entire
 * group" takes a run vanity whole (`assemblyOf`), and a run left owning
 * nothing rebuilds the vanity's cabinetry on the next wall move (2026-11-03).
 * Only when the caller says what remains — without it nothing is dropped —
 * and never a kitchen run, whose record can stand before its fill.
 */
function withoutEmptiedVanityRuns(runs, gone, remainingObjects) {
  if (!Array.isArray(remainingObjects)) return runs;
  const emptied = new Set(
    gone.map((o) => o?.runId).filter((id) => isVanityRunId(id))
  );
  for (const o of remainingObjects) emptied.delete(o?.runId);
  if (!emptied.size) return runs;
  const kept = runs.filter((r) => !emptied.has(r?.id));
  return kept.length === runs.length ? runs : kept;
}

/** The appliance-bay half of `runsAfterObjectsDeleted`. */
function withoutDeletedApplianceBays(runs, gone) {
  let changed = false;
  const out = runs.map((r) => {
    const bays = baysForRun(r);
    const kept = bays.filter(
      (b) =>
        !(
          isApplianceKind(b.kind) &&
          !HOSTED_BAY_KINDS.has(b.kind) &&
          gone.some((o) => bayMatchesObject(b, o))
        )
    );
    if (kept.length === bays.length) return r;
    changed = true;
    const { appliances: _legacy, ...rest } = r;
    return { ...rest, bays: kept };
  });
  return changed ? out : runs;
}

/**
 * The stored shape: `bays` written, legacy `appliances` gone, source kept, and
 * every segment's `orphaned` verdict dropped.
 *
 * `orphaned` is what the frame reconcile SAYS about a segment against the walls
 * of the moment (`wallFrame.js`), not state the run owns. Persisting it outlives
 * the wall's absence: the wall comes back — under a new id after a reprocess,
 * resolving through the stored frame — the reconcile reports `'none'` because
 * the frame matches, and `rehydrateRun` goes on skipping the segment forever.
 * Stripped here because this is the ONE writer of the stored shape.
 */
function storedRunFromRun(run) {
  const { appliances, ...rest } = run;
  const segments = (rest.segments || []).map((s) => {
    if (!s || !('orphaned' in s)) return s;
    const { orphaned, ...seg } = s;
    return seg;
  });
  return {
    ...rest,
    ...(rest.segments ? { segments } : {}),
    bays: baysForRun(run),
  };
}

/**
 * `bay` standing at `anchorParam`, with the seat it states for its appliance
 * (`applianceParam`, a vanity sink bay's bowl) carried the same distance.
 *
 * A vanity's sink bay records WHERE ITS BOWL SITS separately from where its
 * box stands — a scanned bowl is not always in the middle of its cabinet, and
 * the mover seats it there rather than at the box's centre
 * (`vanitySinkBay.js`). So anything that moves a bay must move both, or the
 * cabinet leaves its sink behind: dragging a vanity's sink base in the
 * elevation view moved the box 6" and left the bowl, faucet, mirror and light
 * where they stood (2026-11-02). Every writer of a bay's anchor goes through
 * this; a bay that states no seat just moves.
 */
function bayAt(bay, anchorParam) {
  if (!Number.isFinite(bay?.applianceParam)) return { ...bay, anchorParam };
  if (!Number.isFinite(bay.anchorParam)) return { ...bay, anchorParam };
  return {
    ...bay,
    anchorParam,
    applianceParam: bay.applianceParam + (anchorParam - bay.anchorParam),
  };
}

/**
 * Where a bay's appliance belongs once the fill has SOLVED the bay at
 * `solvedCenter`: the solved centre, or — for a bay that states a seat of its
 * own — that seat, kept where it sits relative to the bay. The fill may stand
 * a bay somewhere other than its stored anchor (a link centres it on a
 * window, a shortened wall clamps it), and the bowl goes with its box there.
 */
function applianceSeatOf(bay, solvedCenter) {
  if (!Number.isFinite(bay?.applianceParam)) return solvedCenter;
  if (!Number.isFinite(bay.anchorParam) || !Number.isFinite(solvedCenter)) {
    return bay.applianceParam;
  }
  return solvedCenter + (bay.applianceParam - bay.anchorParam);
}

module.exports = {
  bayAt,
  applianceSeatOf,
  BAY_KINDS,
  HOSTED_BAY_KINDS,
  SINK_BASE_WIDTHS_IN,
  SINK_BASE_SIDE_ALLOWANCE_IN,
  sinkBaseWidthFor,
  sinkBaseFromWidths,
  APPLIANCE_KIND_BY_RUN_TYPE,
  RUN_TYPE_BY_BAY_KIND,
  FOOTPRINT_BY_TYPE,
  footprintFor,
  footprintForAppliance,
  isApplianceKind,
  isIncumbentSeatedKind,
  applianceBayFromLegacy,
  bayMatchesObject,
  runBayForObject,
  legacyApplianceFromBay,
  baysForRun,
  applianceBays,
  keepOutBays,
  KEEP_OUT_BAY_KINDS,
  freestandingApplianceClaims,
  healRunBayClaims,
  runsAfterObjectsDeleted,
  storedRunFromRun,
};
